import React from 'react';

/**
 * AboutPage component - Information about the platform
 */
const AboutPage = () => {
  return (
    <div className="bg-gray-50">
      {/* Hero section */}
      <section className="bg-primary-700 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">
            About Biotech01
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto">
            Connecting the biotech and health tech ecosystem
          </p>
        </div>
      </section>
      
      {/* Mission section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-lg text-gray-700">
            In recent years we have witnessed firsthand how different the biotech entrepreneurial environment is across the world. In some places, biotech innovation is rapidly transformed into a business concept, and the concept to a fast-growing company, mainly because all stakeholders understand their roles in supporting innovation, whether it be academia, financial institutions, patent attorneys, etc. Unfortunately for most situations, in the majority of countries or cities, this is not the case. Brilliant researchers and innovators may not know or have planned for their current or next groundbreaking invention or discovery to be come a business, or simply don't know how to start. This is a significant unmet need, especially when the usual innovative hotspots of the world does not produce the products some investors or service providers are looking for. At Biotech01, our mission is to bridge the critical gaps to biotech/health tech entrepreneurship by providing the right environment, namely the collection of crucial knowledge, services, funding and partnership opportunities that will help support the growth of innovations to make realworld impact. We believe that transformative health innovations should never remain hidden due to geographical, social, or economic barriers. Our platform strives to connect visionaries directly to the resources, partnerships, and financial backing they need to succeed, ultimately ensuring that impactful healthcare solutions reach patients worldwide.
            </p>
          </div>
          
          <div className="prose prose-lg mx-auto text-gray-600">
            <p>
              Biotech01 started as a Product Development Planning and Valuation platform but has evolved into a comprehensive ecosystem that supports the entire biotech and health tech industry. We recognized that the biggest challenges facing innovative startups weren't just technical or scientific, but also involved connecting with the right partners, investors, and service providers.
            </p><br></br>
            
            <p>
              Today, our platform serves as a vital bridge between:
            </p><br></br>
            
            <ul>
              <li><strong>Biotech and Health Tech Startups</strong> - Innovators developing cutting-edge solutions in pharmaceuticals, medical devices, diagnostics, digital health, and more</li><br></br>
              <li><strong>Investors</strong> - Venture capital firms, angel investors, and strategic corporate investors seeking promising opportunities in the biotech and health tech space</li><br></br>
              <li><strong>Service Providers</strong> - CROs, CDMOs, regulatory consultants, legal experts, and other specialized service providers supporting the industry</li><br></br>
            </ul>
            
            <p>
              Through our platform, we provide tools for development planning, financial analysis, and pitch deck creation, while also facilitating connections, showcasing innovations, and providing resources that help all stakeholders navigate the complex biotech landscape.
            </p>
          </div>
        </div>
      </section>
      
      {/* Team section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Team</h2>
            <p className="text-lg text-gray-700">
              We're a dedicated team of scientists, technologists, and industry experts passionate about advancing biotech innovation.
            </p>
          </div>
          
          {/* Team members would go here - placeholder for now */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 text-center">
              <div className="w-24 h-24 mx-auto mb-4 overflow-hidden rounded-full bg-primary-600 flex items-center justify-center text-white text-xl font-bold">
                VP
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Dr. Veerachat Petpisit</h3>
              <p className="text-gray-600 mb-2">Founder</p>
              <p className="text-sm text-gray-500">
                MD with 30+ years experience in pharmaceutical /healthcare /health-tech /biotech industry.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 text-center">
              <div className="w-24 h-24 mx-auto mb-4 overflow-hidden rounded-full bg-green-600 flex items-center justify-center text-white text-xl font-bold">
                VS
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Vin Sato</h3>
              <p className="text-gray-600 mb-2">Admin & Operations</p>
              <p className="text-sm text-gray-500">
                Detail-oriented and organized with a passion for supporting the biotech and health tech ecosystem.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 text-center">
              <div className="w-24 h-24 mx-auto mb-4 overflow-hidden rounded-full bg-purple-600 flex items-center justify-center text-white text-xl font-bold">
                AP
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Anya Petrov</h3>
              <p className="text-gray-600 mb-2">Programming & Design</p>
              <p className="text-sm text-gray-500">
                Coding wizard with a knack for creating user-friendly interfaces.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Vision section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h2>
            <p className="text-lg text-gray-700">
              Creating a future where biotech and health tech innovation thrives through collaboration and connectivity.
            </p>
          </div>
          
          <div className="prose prose-lg mx-auto text-gray-600">
            <p>
            Our vision is a globally connected biotech and health tech ecosystem where innovative ideas receive the recognition, collaboration, and resources they deserve, irrespective of their origin. By dismantling the existing barriers to innovation, Biotech01 seeks to democratize the biotech and health tech landscape, fostering equitable and widespread adoption of health advancements. We envision a world where every scientist and innovator has the opportunity to impact lives positively through their inventions and discoveries, driving forward a future where groundbreaking healthcare technologies are accessible to patients everywhere.


            </p>
            
            <p>
              By building this connected ecosystem, we aim to accelerate the development of treatments and technologies that improve human health and well-being across the globe.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="py-12 bg-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Join Our Ecosystem
          </h2>
          <p className="text-lg mb-8 max-w-3xl mx-auto">
            Whether you're a startup, investor, or service provider, become part of the Biotech01 community.
          </p>
          <div className="inline-block">
            <a 
              href="/signup" 
              className="bg-white text-primary-700 hover:bg-gray-100 text-center py-3 px-6 rounded-lg text-lg"
            >
              Get Started
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage; 